import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth, API } from '@/App';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  ArrowRight, ArrowUpRight, CaretRight, CheckCircle, EnvelopeSimple, Phone, MapPin
} from '@phosphor-icons/react';
import {
  Animation3DRotation, AnimationCursorDownload,
  AnimationTypingForm, AnimationCountdown
} from './landing/Animations';

/* ============================================================
   HAMAC SOLUTIONS — Site plaquette
   Theme: Light, heavy zinc contrast, deep amber accent (#E66C2C)
   Typography: Fraunces (headings), IBM Plex Sans (body), IBM Plex Mono (labels)
   ============================================================ */

const ACCENT = '#E66C2C';

// ===== HEADER =====
function Header({ scrollToContact }) {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleAccesPro = () => {
    if (isAuthenticated) {
      const role = user?.role;
      navigate(role === 'client' ? '/client' : '/dashboard');
    } else {
      navigate('/login');
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md border-b border-zinc-200' : 'bg-transparent'
      }`}
      data-testid="landing-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="landing-logo">
          <img src="/favicon.svg" alt="HAMAC" className="h-9 w-9" />
          <div className="leading-tight">
            <div className="font-serif text-xl text-zinc-950 tracking-tight">Hamac</div>
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Solutions</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-10 text-sm font-medium text-zinc-700">
          <a href="#produits" className="hover:text-zinc-950 transition-colors" data-testid="nav-produits">Produits</a>
          <a href="#plateforme" className="hover:text-zinc-950 transition-colors" data-testid="nav-plateforme">Plateforme</a>
          <a href="#process" className="hover:text-zinc-950 transition-colors" data-testid="nav-process">Process</a>
          <a
            href="https://hamac-ic.com"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 hover:text-zinc-950 transition-colors"
            data-testid="nav-hamac-ic"
          >
            hamac-ic.com <ArrowUpRight size={13} weight="bold" />
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={scrollToContact}
            className="hidden sm:inline-flex items-center gap-2 text-sm font-medium text-zinc-700 hover:text-zinc-950 transition-colors px-4 py-2"
            data-testid="header-contact-btn"
          >
            Contact
          </button>
          <button
            onClick={handleAccesPro}
            className="inline-flex items-center gap-2 bg-zinc-900 text-white hover:bg-zinc-800 transition-colors px-5 py-2.5 text-sm font-medium"
            data-testid="header-acces-pro-btn"
          >
            Accès Pro <ArrowRight size={14} weight="bold" />
          </button>
        </div>
      </div>
    </header>
  );
}

// ===== HERO =====
function Hero({ scrollToContact }) {
  const navigate = useNavigate();
  return (
    <section className="relative pt-40 pb-32 overflow-hidden" data-testid="hero-section">
      {/* Background zinc texture, subtle */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: 'url(/landing/hero_zinc.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500 mb-6" data-testid="hero-eyebrow">
            Fabricant français — Sur mesure
          </p>
          <h1 className="font-serif font-light text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-zinc-950 tracking-tight">
            L&apos;embout auditif,<br />
            conçu avec
            <span className="italic font-normal" style={{ color: ACCENT }}> l&apos;exigence</span>
            <br />du numérique.
          </h1>
          <p className="mt-10 text-lg text-zinc-600 leading-relaxed max-w-xl">
            Embouts, obturateurs et écouteurs intégrés sur mesure — commandez en ligne,
            visualisez et validez vos fabrications en 3D avant production, suivez leur
            avancement en temps réel et bénéficiez d&apos;une
            <span className="text-zinc-900 font-medium"> garantie de 1 an</span>.
          </p>
          <div className="mt-12 flex flex-col sm:flex-row gap-4">
            <button
              onClick={scrollToContact}
              className="inline-flex items-center justify-center gap-3 text-white px-8 py-4 font-medium tracking-wide transition-colors group"
              style={{ backgroundColor: ACCENT }}
              data-testid="hero-cta-devis"
            >
              Demander un devis
              <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center justify-center gap-3 border border-zinc-900 text-zinc-900 hover:bg-zinc-100 px-8 py-4 font-medium tracking-wide transition-colors"
              data-testid="hero-cta-acces-pro"
            >
              Accès Pro
            </button>
          </div>

          <div className="mt-16 grid grid-cols-3 gap-8 max-w-md">
            <div>
              <div className="font-serif text-3xl text-zinc-950">72h</div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mt-1">Fabrication</div>
            </div>
            <div>
              <div className="font-serif text-3xl text-zinc-950">1 an</div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mt-1">Refab. gratuite</div>
            </div>
            <div>
              <div className="font-serif text-3xl text-zinc-950">100%</div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mt-1">Sur mesure</div>
            </div>
          </div>
        </div>

        {/* Hero visual — 3D rotating mold */}
        <div className="lg:col-span-5 flex justify-center">
          <Animation3DRotation />
        </div>
      </div>
    </section>
  );
}

// ===== PRODUCTS =====
const PRODUCTS = [
  {
    key: 'ric', label: 'RIC',
    title: 'Écouteur déporté',
    blurb: 'Embouts ultra-discrets adaptés à tous les receivers du marché — Phonak, Oticon, Signia, Widex, Starkey…',
    image: '/landing/ric_microcanule.png',
    tags: ['Canule', 'Micro-canule', 'Squelette', 'Pince crabe', '+8 formes'],
  },
  {
    key: 'bte', label: 'BTE',
    title: 'Contour d\'oreille',
    blurb: 'Compatible avec tous les contours classiques — tube standard, tube lock, mini-tubes spécifiques Power SlimTube ou Corda MiniFit.',
    image: '/images/formes/ric_squelette.png',
    tags: ['Tube 3 mm', 'Tube enfant 2.5 mm', 'Tube lock'],
  },
  {
    key: 'integral', label: 'Intégral',
    title: 'Écouteur intégré',
    blurb: 'Embout sur mesure avec écouteur intégré — versions 5.0 / 6.0, puissances S à UP, plusieurs longueurs disponibles.',
    image: '/images/integral_phonak.png',
    tags: ['v5.0 / v6.0', 'Puissance S → UP', 'CeruStop / HF4'],
  },
  {
    key: 'obturateur', label: 'Obturateurs',
    title: 'Protection auditive',
    blurb: 'Anti-bruit filtré (ER, Elacin), anti-bruit non filtré, anti-eau, sommeil, bouchon musique — silicone ou résine.',
    image: '/images/formes/antibruit_filtre.png',
    tags: ['Anti-bruit', 'Anti-eau', 'Sommeil', 'Musique', 'Filtres ER/Elacin'],
  },
];

function ProductsSection() {
  return (
    <section id="produits" className="py-24 md:py-32 bg-white border-t border-zinc-200" data-testid="products-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20">
          <div className="lg:col-span-4">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">01 — Gamme</p>
            <h2 className="font-serif font-light text-4xl md:text-5xl text-zinc-950 leading-tight">
              Quatre familles.<br />
              <span className="italic" style={{ color: ACCENT }}>Une exigence</span>.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6">
            <p className="text-lg text-zinc-600 leading-relaxed">
              Chaque embout est conçu à partir des empreintes du patient et fabriqué dans
              notre atelier en France. Catalogue complet de formes, matières, finitions et options
              — tout est paramétrable depuis votre espace pro.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200">
          {PRODUCTS.map((p, i) => (
            <motion.div
              key={p.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="bg-white p-8 group cursor-default flex flex-col"
              data-testid={`product-card-${p.key}`}
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-6">
                0{i + 1} / {p.label}
              </div>
              <div className="h-48 mb-6 flex items-center justify-center overflow-hidden">
                <img
                  src={p.image}
                  alt={p.title}
                  className="max-h-full object-contain transition-transform duration-500 group-hover:scale-110"
                />
              </div>
              <h3 className="font-serif text-2xl text-zinc-950 leading-tight mb-3">{p.title}</h3>
              <p className="text-sm text-zinc-600 leading-relaxed flex-1">{p.blurb}</p>
              <div className="mt-6 flex flex-wrap gap-1.5">
                {p.tags.map(t => (
                  <span key={t} className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 bg-zinc-100 text-zinc-600">{t}</span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ===== PLATFORM (4 features with animations) =====
const FEATURES = [
  {
    key: 'commande',
    eyebrow: '01',
    title: 'Commande en ligne',
    blurb: 'Formulaire intelligent avec smart defaults par patient et par marque. Tubes, montages, puissances — tout est pré-rempli en quelques clics.',
    Anim: AnimationTypingForm,
  },
  {
    key: '3d',
    eyebrow: '02',
    title: 'Visualisation 3D',
    blurb: 'Avant fabrication, l\'audioprothésiste valide le modèle 3D depuis son portable. Demande de modification possible en un clic.',
    Anim: Animation3DRotation,
  },
  {
    key: 'stl',
    eyebrow: '03',
    title: 'Accès aux moulages',
    blurb: 'Tous vos fichiers STL (empreintes + embouts finaux) sont accessibles à tout moment depuis votre espace — historique complet par patient.',
    Anim: AnimationCursorDownload,
  },
  {
    key: 'garantie',
    eyebrow: '04',
    title: 'Refabrication gratuite',
    blurb: 'Tout embout est garanti 1 an : refabrication identique, modifiée, casse, usure, inconfort — gérée intégralement depuis le module SAV.',
    Anim: AnimationCountdown,
  },
];

function PlatformSection() {
  return (
    <section id="plateforme" className="py-24 md:py-32 bg-zinc-50 border-t border-zinc-200" data-testid="platform-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-24">
          <div className="lg:col-span-5">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">02 — Plateforme</p>
            <h2 className="font-serif font-light text-4xl md:text-5xl text-zinc-950 leading-tight">
              Quatre atouts qui changent<br />
              <span className="italic" style={{ color: ACCENT }}>votre quotidien</span>.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="text-lg text-zinc-600 leading-relaxed">
              Notre plateforme web a été pensée pour les audioprothésistes : moins de
              clics, plus de précision, traçabilité de bout en bout. Vos commandes,
              vos empreintes, vos refabrications — au même endroit, accessibles partout.
            </p>
          </div>
        </div>

        {/* Stacked full-width feature blocks — text on top, GIF below in full width */}
        <div className="space-y-12 md:space-y-16">
          {FEATURES.map((f) => (
            <motion.div
              key={f.key}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.7 }}
              className="bg-white border border-zinc-200"
              data-testid={`feature-${f.key}`}
            >
              {/* Text header */}
              <div className="p-8 md:p-12 border-b border-zinc-100">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-3">
                  {f.eyebrow} — Feature
                </div>
                <h3 className="font-serif text-3xl md:text-4xl text-zinc-950 leading-tight mb-3">{f.title}</h3>
                <p className="text-base md:text-lg text-zinc-600 leading-relaxed max-w-3xl">{f.blurb}</p>
              </div>
              {/* GIF in full width */}
              <div className="bg-zinc-50 flex items-center justify-center p-4 md:p-8">
                <div className="w-full max-w-4xl">
                  <f.Anim />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ===== PROCESS TIMELINE =====
const STEPS = [
  { num: '01', title: 'Saisie', desc: 'Formulaire en ligne — patient, marque, options, STL.' },
  { num: '02', title: 'Validation 3D', desc: 'L\'audio reçoit un lien — valide ou demande une modif.' },
  { num: '03', title: 'Fabrication', desc: 'Atelier — 72h ouvrées (120h pour Intégral).' },
  { num: '04', title: 'Expédition', desc: 'BL + étiquette + suivi colis automatique.' },
  { num: '05', title: 'Garantie', desc: 'Refab. gratuite pendant 12 mois.' },
];

function ProcessSection() {
  return (
    <section id="process" className="py-24 md:py-32 bg-zinc-950 text-white border-t border-zinc-900" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20">
          <div className="lg:col-span-5">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">03 — Process</p>
            <h2 className="font-serif font-light text-4xl md:text-5xl leading-tight">
              De la saisie à la livraison,<br />
              <span className="italic" style={{ color: ACCENT }}>tout est tracé</span>.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="text-lg text-zinc-400 leading-relaxed">
              Cinq étapes claires, un dashboard unique. Vous voyez à tout moment où en est
              chaque commande, qui a agi, et quand. Plus de coups de téléphone perdus à l&apos;atelier.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-zinc-900">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.num}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bg-zinc-950 p-6 md:p-8 relative"
              data-testid={`step-${s.num}`}
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-600 mb-4">Étape {s.num}</div>
              <div className="font-serif text-3xl mb-2" style={{ color: ACCENT }}>—</div>
              <h3 className="font-serif text-xl mb-3">{s.title}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{s.desc}</p>
              {i < STEPS.length - 1 && (
                <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-px bg-zinc-700">
                  <CaretRight size={10} className="absolute -right-2 -top-1.5 text-zinc-700" />
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ===== CONTACT FORM =====
function ContactSection({ formRef }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', cabinet: '', message: '', website: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Merci de remplir nom, email et message');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      toast.success('Demande envoyée — nous revenons vers vous rapidement');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section ref={formRef} id="contact" className="py-24 md:py-32 bg-white border-t border-zinc-200" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500 mb-4">04 — Contact</p>
            <h2 className="font-serif font-light text-4xl md:text-5xl text-zinc-950 leading-tight mb-8">
              Parlons de<br />
              <span className="italic" style={{ color: ACCENT }}>votre cabinet</span>.
            </h2>
            <p className="text-lg text-zinc-600 leading-relaxed mb-10 max-w-md">
              Vous souhaitez ouvrir un compte, obtenir nos tarifs ou simplement
              en savoir plus ? Laissez-nous un message — nous revenons vers vous
              sous 24 h ouvrées.
            </p>
            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <EnvelopeSimple size={20} className="text-zinc-400 mt-0.5" />
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">Email</div>
                  <a href="mailto:contact@hamacsolutions.com" className="text-zinc-900 font-medium hover:underline">contact@hamacsolutions.com</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone size={20} className="text-zinc-400 mt-0.5" />
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">Téléphone</div>
                  <a href="tel:+33677092282" className="text-zinc-900 font-medium hover:underline">06 77 09 22 82</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin size={20} className="text-zinc-400 mt-0.5" />
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">Atelier</div>
                  <div className="text-zinc-900 font-medium leading-relaxed">
                    Hamac<br />
                    67 rue de la République<br />
                    69220 Belleville en Beaujolais
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {success ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="border-2 border-zinc-900 p-12 text-center"
                data-testid="contact-success"
              >
                <CheckCircle size={48} weight="duotone" className="mx-auto mb-6" style={{ color: ACCENT }} />
                <h3 className="font-serif text-3xl text-zinc-950 mb-3">Demande envoyée</h3>
                <p className="text-zinc-600 leading-relaxed max-w-md mx-auto">
                  Merci pour votre intérêt. Un email de confirmation vient de vous être adressé.
                  Notre équipe revient vers vous dans les meilleurs délais.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="contact-form">
                {/* Honeypot */}
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={set('website')}
                  className="absolute -left-[9999px] opacity-0"
                  data-testid="contact-honeypot"
                />
                <div className="sm:col-span-1">
                  <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 block">Nom complet *</Label>
                  <Input
                    value={form.name}
                    onChange={set('name')}
                    placeholder="Marie Dupont"
                    required
                    className="rounded-none border-zinc-300 bg-white h-12"
                    data-testid="contact-name"
                  />
                </div>
                <div className="sm:col-span-1">
                  <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 block">Email *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={set('email')}
                    placeholder="marie@cabinet-audio.fr"
                    required
                    className="rounded-none border-zinc-300 bg-white h-12"
                    data-testid="contact-email"
                  />
                </div>
                <div className="sm:col-span-1">
                  <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 block">Téléphone</Label>
                  <Input
                    type="tel"
                    value={form.phone}
                    onChange={set('phone')}
                    placeholder="06 12 34 56 78"
                    className="rounded-none border-zinc-300 bg-white h-12"
                    data-testid="contact-phone"
                  />
                </div>
                <div className="sm:col-span-1">
                  <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 block">Cabinet / société</Label>
                  <Input
                    value={form.cabinet}
                    onChange={set('cabinet')}
                    placeholder="Audition Pluriel"
                    className="rounded-none border-zinc-300 bg-white h-12"
                    data-testid="contact-cabinet"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2 block">Votre message *</Label>
                  <Textarea
                    value={form.message}
                    onChange={set('message')}
                    rows={6}
                    placeholder="Présentez-nous votre cabinet, vos volumes, vos besoins…"
                    required
                    className="rounded-none border-zinc-300 bg-white"
                    data-testid="contact-message"
                  />
                </div>
                <div className="sm:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4">
                  <p className="text-xs text-zinc-500 max-w-md">
                    En envoyant ce formulaire, vous acceptez d&apos;être recontacté par Hamac Solutions.
                    Aucune donnée n&apos;est partagée avec des tiers.
                  </p>
                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center justify-center gap-3 text-white px-8 py-4 font-medium tracking-wide transition-colors disabled:opacity-60 disabled:cursor-not-allowed group"
                    style={{ backgroundColor: ACCENT }}
                    data-testid="contact-submit"
                  >
                    {loading ? 'Envoi…' : 'Envoyer la demande'}
                    {!loading && <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ===== FOOTER =====
function Footer() {
  return (
    <footer className="bg-zinc-950 text-zinc-400 py-16 border-t border-zinc-900" data-testid="landing-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <img src="/favicon.svg" alt="HAMAC" className="h-9 w-9 invert" />
              <div className="leading-tight">
                <div className="font-serif text-xl text-white tracking-tight">Hamac</div>
                <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">Solutions</div>
              </div>
            </div>
            <p className="text-sm leading-relaxed max-w-sm">
              Fabricant français d&apos;embouts auditifs sur mesure pour audioprothésistes.
              Plateforme numérique de bout en bout — commande, 3D, suivi, garantie.
            </p>
          </div>
          <div className="md:col-span-2">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600 mb-4">Plateforme</p>
            <ul className="space-y-3 text-sm">
              <li><a href="#produits" className="hover:text-white transition-colors">Produits</a></li>
              <li><a href="#plateforme" className="hover:text-white transition-colors">Atouts</a></li>
              <li><a href="#process" className="hover:text-white transition-colors">Process</a></li>
              <li><a href="#contact" className="hover:text-white transition-colors">Contact</a></li>
            </ul>
          </div>
          <div className="md:col-span-2">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600 mb-4">Groupe</p>
            <ul className="space-y-3 text-sm">
              <li>
                <a href="https://hamac-ic.com" target="_blank" rel="noreferrer" className="hover:text-white transition-colors inline-flex items-center gap-1">
                  hamac-ic.com <ArrowUpRight size={11} />
                </a>
              </li>
              <li><Link to="/login" className="hover:text-white transition-colors">Accès Pro</Link></li>
              <li><Link to="/cgv" className="hover:text-white transition-colors" data-testid="footer-link-cgv">CGV</Link></li>
              <li><Link to="/confidentialite" className="hover:text-white transition-colors">Confidentialité</Link></li>
              <li><Link to="/mentions-legales" className="hover:text-white transition-colors" data-testid="footer-link-mentions-legales">Mentions légales</Link></li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600 mb-4">Contact</p>
            <ul className="space-y-3 text-sm">
              <li><a href="mailto:contact@hamacsolutions.com" className="hover:text-white transition-colors">contact@hamacsolutions.com</a></li>
              <li><a href="tel:+33677092282" className="hover:text-white transition-colors">06 77 09 22 82</a></li>
              <li className="leading-relaxed">
                67 rue de la République<br />
                69220 Belleville en Beaujolais
              </li>
            </ul>
          </div>
        </div>
        <div className="pt-8 border-t border-zinc-900 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-zinc-500">
          <p>© {new Date().getFullYear()} Hamac Solutions. Tous droits réservés.</p>
          <p className="font-mono uppercase tracking-widest text-[10px]">Made in France</p>
        </div>
      </div>
    </footer>
  );
}

// ===== MAIN PAGE =====
export default function LandingPage() {
  const contactRef = useRef(null);
  const scrollToContact = () => contactRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="bg-white text-zinc-900 min-h-screen font-sans antialiased landing-root">
      <Header scrollToContact={scrollToContact} />
      <main>
        <Hero scrollToContact={scrollToContact} />
        <ProductsSection />
        <PlatformSection />
        <ProcessSection />
        <ContactSection formRef={contactRef} />
      </main>
      <Footer />
    </div>
  );
}
